<template>
  <div class="board-list">
    <board-list-header />
    <div
      v-if="getIsProjectLoaded"
      class="list"
      @contextmenu.prevent.self="onContextMenu($event)"
    >
      <vddl-list
        class="panel__body--list"
        :class="{ dragover: dragOver }"
        :list="rootContainedBoards"
        :drop="onDropped"
        :external-sources="true"
        :allowed-types="allowedTypes"
        :disable-if="disable || !canEdit"
      >
        <board-list-item
          v-for="(item, index) in rootContainedBoards"
          :key="item.id"
          v-model:name="item.name"
          :item="item"
          :list="rootContainedBoards"
          :index="index"
          :container-id="getRootContainer.id"
        />
      </vddl-list>
      <div v-if="canEdit" class="create_new">
        <div class="board" @click="onClickCreate">
          <div class="icon">
            <svg-icon
              fill="#737575"
              :width="'14'"
              :height="'14'"
              icon="app-icons/create"
            />
          </div>
          <span>New board</span>
        </div>
        <div class="folder" @click="onClickCreateFolder">
          <svg-icon
            fill="#bcbcbc"
            :width="'13'"
            :height="'13'"
            icon="app-icons/folder-add"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Vddl from 'vddl'; // Library For creating the tree structure
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, EVENT, DRAG_TYPE, DRAG_LIST } from '@/constants.js';
import BoardListHeader from './BoardListHeader.vue';
import BoardListItem from './BoardListItem.vue';
import SvgIcon from '../all/SvgIcon.vue';
import boardTypes from '../../vuex/project/types/boards-types.js';

export default {
  components: {
    SvgIcon,
    'board-list-item': BoardListItem,
    'board-list-header': BoardListHeader,
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      disable: false,
      collapsedItem: null,
      allowedTypes: [DRAG_TYPE.BOARD, DRAG_TYPE.BOARD_CONTAINER],
      dragOver: false,
    };
  },

  computed: {
    ...mapGetters('project/data/boards', [
      'getContainedNodes',
      'getRootContainer',
    ]),
    ...mapGetters('project/config/boards', ['getSelections']),
    ...mapGetters('application/drag', ['getIsEmpty']),
    ...mapGetters('application/appStates', ['getIsProjectLoaded']),

    rootContainedBoards() {
      if (!this.getRootContainer) return [];

      return this.getContainedNodes(
        this.getRootContainer.id,
        'boards',
        'children'
      );
    },
  },

  watch: {
    getIsEmpty(newVal, oldVal) {
      if (newVal) {
        this.dragOver = false;
      }
    },
  },

  mounted() {
    $(this.$el).on('dragenter', event => {
      this.onDragEnter(event);
    });
    $(this.$el).on('dragleave', event => {
      this.onDragLeave(event);
    });

    $(this.$el).resizable({
      minHeight: 41,
      maxHeight: $(window).height() - 41,
      handles: 'n',
    });
  },

  unmounted() {
    $(this.$el).off('dragenter');
    $(this.$el).off('dragleave');
  },

  methods: {
    ...mapActions('project/data/boards', ['customCreate']),
    ...mapActions('application/drag', ['setTarget']),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),
    renameBoard() {
      const input = $(this.$el).find('.name')[0];
      input.readOnly = false;
      input.select();
    },

    /**
     * Event handler for vddl drop event
     */
    onDropped(draggable) {
      this.setTarget({
        value: {
          type: DRAG_LIST.BOARDS,
          id: this.item === undefined ? this.getRootContainer.id : this.item.id,
          index: draggable.index,
        },
      });
    },

    /**
     * Event handler for dragenter event. Something is being dragged over the list.
     */
    onDragEnter(event) {
      this.dragOver = true;
    },

    /**
     * Event handler for dragleave event. Something is being dragged out of the list.
     */
    onDragLeave(event) {
      const rect = event.currentTarget.getBoundingClientRect();

      // If the actual coordinates are outside the list area (the drag leave should ignore children)
      if (
        event.clientY <= rect.top ||
        event.clientY >= rect.bottom ||
        event.clientX <= rect.left ||
        event.clientX >= rect.right
      ) {
        this.dragOver = false;
      }
    },

    /**
     * Event listener - click on create board button.
     * Create a new board and open it.
     */
    onClickCreate() {
      this.customCreate({
        board: boardTypes.newItem,
        tIndex: '-',
        apply: true,
      });
    },

    /**
     * Event handler - click on New Folder button
     */
    onClickCreateFolder() {
      this.customCreate({
        board: boardTypes.newContainer,
        tIndex: '-',
        apply: true,
      });
    },

    /**
     * Event handler for contextmenu event.
     */
    onContextMenu(event) {
      if (this.canEdit) {
        this.createContextMenu(event.clientX, event.clientY);
      }
    },

    /**
     * Create a context menu for this board
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      this.createItem({
        name: 'Create Board',
        icon: null,
        commands: {
          doAction: () => {
            this.customCreate({
              board: boardTypes.newItem,
              tIndex: '-',
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });
      this.createItem({
        name: 'Create Folder',
        icon: null,
        commands: {
          doAction: () => {
            this.customCreate({
              board: boardTypes.newContainer,
              tIndex: '-',
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.sidebar .board-list {
  width: 100%;
  align-self: flex-end;
  height: calc(50% - 17px);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  position: relative !important;
  top: auto !important;
}

.create_new {
  cursor: pointer;
  display: flex;
  // padding-left: 6px;
  align-items: center;
  height: 30px;

  &:hover {
    background: #353c3e;
    .folder {
      opacity: 1;
    }
  }
  .board {
    padding-left: 6px;
    align-items: center;
    display: flex;
    flex: 1;
    height: 100%;
    &:hover {
      span {
        opacity: 1;
      }
      :deep(.icon svg path) {
        fill: #bcbcbc !important;
      }
    }
  }
  .folder {
    opacity: 0;
    display: flex;
    padding: 6px 6px;
    margin: 0 10px 0 0;
    border-radius: 4px;
    &:hover {
      background: #2c3132;
    }
  }

  .icon {
    width: 22px;
    height: 22px;
    margin: 0 6px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  span {
    user-select: none;
    flex: 1 1 50%;
    display: block;
    color: #bcbcbc;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    font-size: 12px;
    opacity: 0.7;
  }
}
</style>
